<template>
  <div class="modal fade show d-block task-editor-backdrop" tabindex="-1" role="dialog" @mousedown.self="requestClose">
    <div class="modal-dialog modal-xl modal-dialog-centered task-editor-dialog" role="document">
      <div class="modal-content task-editor-modal">
        <form class="task-editor-form" @submit.prevent="submit">
          <header class="task-editor-header">
            <div class="task-heading">
              <div class="task-check" :class="{ 'is-complete': form.status === 'completed' }">
                <AppIcon :name="form.status === 'completed' ? 'bi-check-lg' : 'bi-list-check'" class="bi" :class="form.status === 'completed' ? 'bi-check-lg' : 'bi-list-check'" />
              </div>
              <div class="task-heading-copy">
                <input ref="titleInput" v-model.trim="form.title" class="task-title-input" type="text" required maxlength="255" :disabled="!canModifyTask" :placeholder="$t('title')" :aria-label="$t('title')">
                <div class="task-project-name">
                  <AppIcon name="bi-folder" class="bi bi-folder" />
                  <span>{{ projectName || $t('project') }}</span>
                </div>
              </div>
            </div>

            <div class="task-header-actions">
              <button v-if="canEditTask" type="button" class="task-header-action" :disabled="!task || uploading" :title="$t('attachments')" @click="openFilePicker">
                <span v-if="uploading" class="spinner-border spinner-border-sm"></span>
                <AppIcon name="bi-paperclip" v-else class="bi bi-paperclip" />
                <span>{{ attachmentCount }}</span>
              </button>
              <input ref="attachmentInput" class="visually-hidden" type="file" @change="uploadAttachment">
              <span class="task-action-divider"></span>
              <button v-if="canEditTask" type="button" class="task-header-action task-time-summary" :disabled="!task" :title="$t('log_time')" @click="toggleTimeTracker">
                <AppIcon name="bi-clock" class="bi bi-clock" />
                <span>{{ formatHours(loggedHours) }}</span>
              </button>
              <button type="button" class="task-icon-button" :class="{ active: showActionsMenu }" :aria-label="$t('additional_actions')" @click="toggleActionsMenu">
                <AppIcon name="bi-three-dots" class="bi bi-three-dots" />
              </button>
              <span class="task-action-divider"></span>
              <button type="button" class="task-icon-button task-close-button" :aria-label="$t('close')" @click="requestClose">
                <AppIcon name="bi-x-lg" class="bi bi-x-lg" />
              </button>
            </div>

            <div v-if="showTimeTracker" class="task-header-popover time-tracker-popover">
              <strong>{{ $t('log_time') }}</strong>
              <label>
                <span>{{ $t('hours') }}</span>
                <input v-model.number="timeLog.hours" type="number" min="0.25" step="0.25" @keydown.enter.prevent="logTime">
              </label>
              <label>
                <span>{{ $t('description') }}</span>
                <input v-model.trim="timeLog.description" type="text" @keydown.enter.prevent="logTime">
              </label>
              <button type="button" class="btn btn-success btn-sm" :disabled="loggingTime || !timeLog.hours" @click="logTime">
                <span v-if="loggingTime" class="spinner-border spinner-border-sm me-1"></span>{{ $t('save') }}
              </button>
            </div>

            <div v-if="showActionsMenu" class="task-header-popover task-actions-menu">
              <button type="button" @click="toggleMoreDetails">
                <AppIcon name="bi-sliders" class="bi bi-sliders" />
                <span>{{ $t('more_details') }}</span>
              </button>
              <button v-if="canEditTask" type="button" :disabled="!task || uploading" @click="openFilePickerFromMenu">
                <AppIcon name="bi-paperclip" class="bi bi-paperclip" />
                <span>{{ $t('attachments') }}</span>
              </button>
            </div>
          </header>

          <div class="task-meta-row">
            <label class="task-pill status-pill" :style="statusPillStyle">
              <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
              <select v-model="form.status" :disabled="!canModifyTask" :aria-label="$t('status')">
                <option v-for="status in availableStatuses" :key="status.value" :value="status.value">{{ status.label }}</option>
              </select>
              <AppIcon name="bi-chevron-down" class="bi bi-chevron-down pill-chevron" />
            </label>

            <label class="task-pill priority-pill" :class="'priority-' + form.priority">
              <AppIcon name="bi-exclamation-triangle-fill" class="bi bi-exclamation-triangle-fill" />
              <select v-model="form.priority" :disabled="!canModifyTask" :aria-label="$t('priority')">
                <option value="low">{{ $t('low') }}</option>
                <option value="medium">{{ $t('medium') }}</option>
                <option value="high">{{ $t('high') }}</option>
                <option value="urgent">{{ $t('urgent') }}</option>
              </select>
              <AppIcon name="bi-chevron-down" class="bi bi-chevron-down pill-chevron" />
            </label>

            <label class="task-pill date-pill">
              <AppIcon name="bi-calendar3" class="bi bi-calendar3" />
              <input v-model="form.due_date" type="date" :disabled="!canModifyTask" :aria-label="$t('due_date')">
            </label>
          </div>

          <div class="task-editor-body">
            <section class="task-details-column">
              <div class="task-section">
                <label class="task-section-title" for="task-description">{{ $t('description') }}</label>
                <textarea id="task-description" v-model="form.description" class="task-description-input" rows="3" :disabled="!canModifyTask" :placeholder="$t('description')"></textarea>
              </div>

              <div class="task-field-grid">
                <div class="task-field">
                  <label class="task-field-label">{{ $t('assigned_to') }}</label>
                  <div class="assignee-chips">
                    <div v-for="(assignedUser, index) in form.assigned_users" :key="assignedUser.user_id || index" class="assignee-chip-wrap">
                      <div class="assignee-chip" :class="{ active: editingAssigneeIndex === index, removing: pendingAssigneeRemovalIndex === index }">
                        <button type="button" class="assignee-chip-main" :disabled="!canModifyTask" :aria-label="$t('change_role')" @click="toggleRoleEditor(index)">
                          <span class="user-avatar">{{ userInitial(assignedUser.user_id) }}</span>
                          <span>{{ userName(assignedUser.user_id) }}</span>
                        </button>
                        <template v-if="pendingAssigneeRemovalIndex === index">
                          <button type="button" class="confirm-chip-action" :title="$t('confirm_remove')" @click="confirmRemoveUser(index)"><AppIcon name="bi-check" class="bi bi-check" /></button>
                          <button type="button" class="cancel-chip-action" :title="$t('cancel')" @click="pendingAssigneeRemovalIndex = null"><AppIcon name="bi-arrow-counterclockwise" class="bi bi-arrow-counterclockwise" /></button>
                        </template>
                        <button v-else-if="canModifyTask" type="button" class="remove-assignment" :aria-label="$t('remove_assignee')" @click="requestRemoveUser(index)"><AppIcon name="bi-x" class="bi bi-x" /></button>
                      </div>
                      <div v-if="editingAssigneeIndex === index" class="role-editor">
                        <label :for="'assignee-role-' + index">{{ $t('role') }}</label>
                        <select :id="'assignee-role-' + index" v-model="assignedUser.role" @change="editingAssigneeIndex = null">
                          <option value="viewer">{{ $t('viewer') }}</option>
                          <option value="contributor">{{ $t('contributor') }}</option>
                          <option value="manager">{{ $t('manager') }}</option>
                        </select>
                      </div>
                    </div>
                    <button v-if="canModifyTask && availableUnassignedUsers.length" type="button" class="add-assignee-button" :aria-label="$t('add_assignee')" @click="showAssigneePicker = !showAssigneePicker"><AppIcon name="bi-plus" class="bi bi-plus" /></button>
                  </div>
                  <div v-if="showAssigneePicker" class="assignee-picker">
                    <select v-model="pendingAssigneeId" :aria-label="$t('select_user')">
                      <option value="">{{ $t('select_user') }}</option>
                      <option v-for="user in availableUnassignedUsers" :key="user.id" :value="user.id">{{ user.name }}</option>
                    </select>
                    <button type="button" class="btn btn-primary btn-sm" :disabled="!pendingAssigneeId" @click="confirmAddUser">{{ $t('add') }}</button>
                  </div>
                  <span v-if="!form.assigned_users.length && !showAssigneePicker" class="empty-assignee-copy">{{ $t('no_users_assigned') }}</span>
                </div>

                <div class="task-field">
                  <label class="task-field-label" for="task-due-date">{{ $t('due_date') }}</label>
                  <div class="date-control">
                    <AppIcon name="bi-calendar3" class="bi bi-calendar3" />
                    <input id="task-due-date" v-model="form.due_date" type="date" :disabled="!canModifyTask">
                  </div>
                </div>
              </div>

              <section class="subtasks-section" :class="{ 'has-subtasks': subtasks.length }">
                <div class="subtasks-heading">
                  <div>
                    <h6>{{ $t('subtasks') }}</h6>
                    <span v-if="subtasks.length">{{ completedSubtasks }}/{{ subtasks.length }}</span>
                  </div>
                  <button v-if="canModifyTask" type="button" class="add-subtask-action" @click="showSubtaskInput = true"><AppIcon name="bi-plus" class="bi bi-plus" />{{ $t('add_subtask') }}</button>
                </div>
                <div v-if="subtasks.length" class="subtask-progress" aria-hidden="true">
                  <span :style="{ width: subtaskProgress + '%' }"></span>
                </div>
                <div v-if="subtasks.length" class="subtask-list">
                  <div v-for="subtask in orderedSubtasks" :key="subtask.id" class="subtask-item" :class="{ completed: subtask.is_completed }">
                    <input type="checkbox" :checked="subtask.is_completed" :disabled="!canModifyTask || togglingSubtaskId === subtask.id" :aria-label="subtask.title" @change="toggleSubtask(subtask, $event.target.checked)">
                    <span class="subtask-title">{{ subtask.title }}</span>
                    <template v-if="canModifyTask && pendingSubtaskRemovalId === subtask.id">
                      <button type="button" class="subtask-confirm" :title="$t('confirm_remove')" @click="deleteSubtask(subtask)"><AppIcon name="bi-check" class="bi bi-check" /></button>
                      <button type="button" class="subtask-cancel" :title="$t('cancel')" @click="pendingSubtaskRemovalId = null"><AppIcon name="bi-arrow-counterclockwise" class="bi bi-arrow-counterclockwise" /></button>
                    </template>
                    <button v-else-if="canModifyTask" type="button" class="subtask-remove" :aria-label="$t('remove')" @click="pendingSubtaskRemovalId = subtask.id"><AppIcon name="bi-trash3" class="bi bi-trash3" /></button>
                  </div>
                </div>
                <div v-if="showSubtaskInput" class="subtask-input-row">
                  <input ref="subtaskInput" v-model.trim="newSubtaskTitle" type="text" maxlength="255" :placeholder="$t('new_subtask')" @keydown.enter.prevent="addSubtask" @keydown.esc.prevent="cancelSubtaskInput">
                  <button type="button" class="btn btn-primary btn-sm" :disabled="savingSubtask || !newSubtaskTitle" @click="addSubtask">
                    <span v-if="savingSubtask" class="spinner-border spinner-border-sm"></span><span v-else>{{ $t('add') }}</span>
                  </button>
                  <button type="button" class="btn btn-light btn-sm" @click="cancelSubtaskInput">{{ $t('cancel') }}</button>
                </div>
              </section>

              <transition name="details-slide">
                <div v-if="showMoreDetails" class="task-more-details">
                  <div class="task-field-grid">
                    <div class="task-field">
                      <label class="task-field-label" for="task-start-date">{{ $t('start_date') }}</label>
                      <div class="date-control"><AppIcon name="bi-calendar-event" class="bi bi-calendar-event" /><input id="task-start-date" v-model="form.start_date" type="date" :disabled="!canModifyTask"></div>
                    </div>
                    <div class="task-field">
                      <label class="task-field-label" for="task-hours">{{ $t('estimated_hours') }}</label>
                      <div class="date-control"><AppIcon name="bi-clock" class="bi bi-clock" /><input id="task-hours" v-model.number="form.estimated_hours" type="number" min="0" step="0.5" :disabled="!canModifyTask"></div>
                    </div>
                  </div>
                  <div class="task-field mt-3">
                    <collapsible-notes-field v-model="form.notes" id="task-notes" rows="2" :disabled="!canModifyTask" />
                  </div>
                </div>
              </transition>

              <div v-if="task && task.attachments && task.attachments.length" class="task-attachments">
                <div class="progress-heading"><label>{{ $t('attachments') }}</label><span>{{ task.attachments.length }}</span></div>
                <div class="attachment-list">
                  <div v-for="attachment in task.attachments" :key="attachment.id" class="attachment-item">
                    <a href="#" :title="$t('preview')" @click.prevent="previewAttachment(attachment)">
                      <AppIcon name="bi-file-earmark" class="bi bi-file-earmark" /><span>{{ attachment.filename }}</span><AppIcon name="bi-lock-fill" v-if="attachment.is_private" class="bi bi-lock-fill attachment-private" :title="$t('private')" />
                    </a>
                    <button type="button" :title="$t('download')" @click="downloadAttachment(attachment)"><AppIcon name="bi-download" class="bi bi-download" /></button>
                    <button v-if="canEditTask" type="button" class="attachment-delete" :disabled="deletingAttachmentId === attachment.id" :title="$t('delete')" @click="deleteAttachment(attachment)">
                      <span v-if="deletingAttachmentId === attachment.id" class="spinner-border spinner-border-sm"></span><AppIcon name="bi-trash3" v-else class="bi bi-trash3" />
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section class="task-activity-column">
              <div class="task-section comments-section">
                <h6 class="task-section-title">{{ $t('comments') }}</h6>
                <div v-if="canEditTask" class="comment-composer" :class="{ disabled: !task }">
                  <span class="comment-avatar">{{ currentUserInitial }}</span>
                  <textarea v-model="newComment" rows="2" :disabled="!task || sendingComment" :placeholder="task ? $t('add_comment') + '…' : $t('save') + ' ' + $t('task')" @keydown.ctrl.enter.prevent="addComment" @keydown.meta.enter.prevent="addComment"></textarea>
                  <div class="comment-actions">
                    <div class="comment-tools">
                      <button type="button" :disabled="!task || uploading" :aria-label="$t('attachments')" @click="openFilePicker"><AppIcon name="bi-paperclip" class="bi bi-paperclip" /></button>
                      <span class="comment-hint">Ctrl + Enter</span>
                    </div>
                    <button type="button" class="send-comment" :disabled="!task || !newComment.trim() || sendingComment" @click="addComment">
                      <span v-if="sendingComment" class="spinner-border spinner-border-sm"></span><span v-else>{{ $t('send') }}</span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="activity-section">
                <div class="activity-heading"><h6 class="task-section-title mb-0">{{ $t('activity_log') }}</h6><span>{{ sortedActivities.length }}</span></div>
                <div v-if="sortedActivities.length" class="activity-list" :class="{ expanded: showAllActivity }">
                  <article v-for="activity in visibleActivities" :key="activity.id" class="activity-item">
                    <span class="activity-avatar">{{ activityInitial(activity) }}</span>
                    <div class="activity-copy">
                      <div class="activity-meta"><strong>{{ activityUserName(activity) }}</strong><time>{{ formatActivityDate(activity.created_at) }}</time></div>
                      <div v-if="activity.activity_type === 'status_change' && (activity.old_value || activity.new_value)" class="activity-action status-change">
                        <span class="activity-action-label">{{ $t('status_changed') }}</span>
                        <span class="status-badge">{{ statusLabel(activity.old_value) }}</span><AppIcon name="bi-arrow-right" class="bi bi-arrow-right" /><span class="status-badge new-status">{{ statusLabel(activity.new_value) }}</span>
                      </div>
                      <p v-else>{{ activityAction(activity) }}</p>
                      <span v-if="activity.hours_logged > 0" class="hours-logged"><AppIcon name="bi-clock" class="bi bi-clock" />{{ formatHours(activity.hours_logged) }}</span>
                    </div>
                  </article>
                </div>
                <div v-else class="empty-activity"><AppIcon name="bi-clock-history" class="bi bi-clock-history" /><p>{{ $t('no_activities_yet') }}</p></div>
                <button v-if="sortedActivities.length > 3" type="button" class="history-toggle" @click="showAllActivity = !showAllActivity">
                  {{ showAllActivity ? $t('show_less_history') : $t('view_full_history', { count: sortedActivities.length }) }}
                  <AppIcon :name="showAllActivity ? 'bi-chevron-up' : 'bi-chevron-down'" class="bi" :class="showAllActivity ? 'bi-chevron-up' : 'bi-chevron-down'" />
                </button>
              </div>
            </section>
          </div>

          <footer class="task-editor-footer">
            <button v-if="task && canDeleteTask" type="button" class="delete-task-button" :disabled="deleting" @click="deleteTask">
              <span v-if="deleting" class="spinner-border spinner-border-sm"></span><AppIcon name="bi-trash3" v-else class="bi bi-trash3" /><span>{{ $t('delete') }}</span>
            </button>
            <span v-else></span>
            <div class="footer-actions">
              <button type="button" class="btn btn-secondary" @click="requestClose">{{ $t('cancel') }}</button>
              <button v-if="canModifyTask" type="submit" class="btn btn-success" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span><span>{{ task ? $t('save') : $t('create') }}</span>
              </button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper'
import { getTaskStatuses, getTaskStatusLabel } from '../../helpers/taskStatusHelper'

export default {
  name: 'AddTaskModal',
  props: {
    projectId: { type: String, required: true },
    task: { type: Object, default: null }
  },
  data() {
    return {
      form: {
        project_id: this.projectId,
        title: '',
        description: '',
        status: 'todo',
        priority: 'medium',
        start_date: '',
        due_date: '',
        estimated_hours: 0,
        progress: 0,
        notes: '',
        assigned_users: []
      },
      users: [],
      projectName: '',
      loadingUsers: false,
      loading: false,
      deleting: false,
      uploading: false,
      deletingAttachmentId: null,
      sendingComment: false,
      newComment: '',
      showMoreDetails: false,
      showActionsMenu: false,
      showTimeTracker: false,
      timeLog: {
        hours: '',
        description: ''
      },
      loggingTime: false,
      showAssigneePicker: false,
      pendingAssigneeId: '',
      editingAssigneeIndex: null,
      pendingAssigneeRemovalIndex: null,
      subtasks: [],
      showSubtaskInput: false,
      newSubtaskTitle: '',
      savingSubtask: false,
      togglingSubtaskId: null,
      pendingSubtaskRemovalId: null,
      showAllActivity: false,
      initialFormSnapshot: '',
      checkingUnsavedChanges: false
    }
  },
  computed: {
    canCreateTask() {
      return helper.checkAccess('create_tasks') > 0
    },
    canEditTask() {
      return helper.checkAccess('edit_tasks') > 0
    },
    canModifyTask() {
      return this.task ? this.canEditTask : this.canCreateTask
    },
    canDeleteTask() {
      return helper.checkAccess('delete_tasks') > 0
    },
    availableStatuses() {
      return getTaskStatuses(this.$store).map(status => ({
        ...status,
        label: this.translateStatus(status.value, status.label)
      }))
    },
    selectedStatus() {
      return this.availableStatuses.find(status => status.value === this.form.status) || this.availableStatuses[0]
    },
    statusPillStyle() {
      const color = this.selectedStatus ? this.selectedStatus.color : '#64748b'
      return { '--task-status-color': color, '--task-status-bg': this.hexToRgba(color, 0.13) }
    },
    attachmentCount() {
      return this.task && this.task.attachments ? this.task.attachments.length : 0
    },
    sortedActivities() {
      if (!this.task || !this.task.activities) return []
      return [...this.task.activities].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },
    visibleActivities() {
      return this.showAllActivity ? this.sortedActivities : this.sortedActivities.slice(0, 3)
    },
    availableUnassignedUsers() {
      const assignedIds = this.form.assigned_users.map(item => String(item.user_id))
      return this.users.filter(user => !assignedIds.includes(String(user.id)))
    },
    orderedSubtasks() {
      return [...this.subtasks].sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
    },
    completedSubtasks() {
      return this.subtasks.filter(subtask => subtask.is_completed).length
    },
    subtaskProgress() {
      return this.subtasks.length ? Math.round((this.completedSubtasks / this.subtasks.length) * 100) : 0
    },
    loggedHours() {
      return this.task ? Number(this.task.actual_hours) || 0 : 0
    },
    hasUnsavedChanges() {
      return Boolean(this.initialFormSnapshot) && this.serializedForm() !== this.initialFormSnapshot
    },
    currentUserInitial() {
      const user = this.$store.getters.USER
      return user && user.name ? user.name.charAt(0).toUpperCase() : 'U'
    }
  },
  mounted() {
    this.populateForm()
    this.initialFormSnapshot = this.serializedForm()
    this.loadUsers()
    document.addEventListener('keydown', this.handleEscape)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscape)
  },
  methods: {
    populateForm() {
      if (!this.task) return
      this.form.title = this.task.title || ''
      this.form.description = this.task.description || ''
      this.form.status = this.task.status || 'todo'
      this.form.priority = this.task.priority || 'medium'
      this.form.start_date = this.task.start_date ? this.formatDateForInput(this.task.start_date) : ''
      this.form.due_date = this.task.due_date ? this.formatDateForInput(this.task.due_date) : ''
      this.form.estimated_hours = Number(this.task.estimated_hours) || 0
      this.form.progress = Number(this.task.progress) || 0
      this.form.notes = this.task.notes || ''
      this.form.assigned_users = (this.task.assignments || []).map(assignment => ({ user_id: assignment.user_id, role: assignment.role || 'contributor' }))
      this.subtasks = (this.task.subtasks || []).map(subtask => ({ ...subtask }))
    },
    async loadUsers() {
      this.projectName = this.task && this.task.project ? this.task.project.name || '' : ''
      if (!this.canModifyTask) return
      this.loadingUsers = true
      try {
        if (this.task) {
          const response = await helper.customGet(`${process.env.VUE_APP_API}/tasks/${this.task.id}/project-users`)
          this.projectName = response.data.project.name || this.projectName
          this.users = response.data.users || []
        } else {
          const response = await helper.customGet(`${process.env.VUE_APP_API}/projects/${this.projectId}/task-users`)
          this.projectName = response.data.project.name || ''
          this.users = response.data.users || []
        }
      } catch (error) {
        this.$toast.error(error.response && error.response.data ? error.response.data.message : this.$t('error_loading_project_members'))
      } finally {
        this.loadingUsers = false
      }
    },
    confirmAddUser() {
      if (!this.canModifyTask || !this.pendingAssigneeId) return
      this.form.assigned_users.push({ user_id: this.pendingAssigneeId, role: 'contributor' })
      this.pendingAssigneeId = ''
      this.showAssigneePicker = false
    },
    toggleRoleEditor(index) {
      if (!this.canModifyTask) return
      this.pendingAssigneeRemovalIndex = null
      this.editingAssigneeIndex = this.editingAssigneeIndex === index ? null : index
    },
    requestRemoveUser(index) {
      if (!this.canModifyTask) return
      this.editingAssigneeIndex = null
      this.pendingAssigneeRemovalIndex = index
    },
    confirmRemoveUser(index) {
      if (!this.canModifyTask) return
      this.form.assigned_users.splice(index, 1)
      this.pendingAssigneeRemovalIndex = null
    },
    async submit() {
      if (!this.canModifyTask) return
      const title = this.form.title.trim()
      if (!title) {
        this.form.title = ''
        this.$nextTick(() => this.$refs.titleInput && this.$refs.titleInput.focus())
        return
      }
      this.form.title = title
      this.loading = true
      try {
        if (this.task) {
          await helper.customPatch(`${process.env.VUE_APP_API}/tasks/${this.task.id}`, this.form, { showToast: true })
          await this.updateAssignments(this.task.id)
        } else {
          await helper.customPost(`${process.env.VUE_APP_API}/tasks`, {
            ...this.form,
            subtasks: this.subtasks.map(subtask => ({ title: subtask.title, is_completed: subtask.is_completed }))
          }, { showToast: true })
        }
        this.initialFormSnapshot = this.serializedForm()
        this.$emit('refresh')
        this.closeImmediately()
      } catch (error) {
        this.$toast.error(error.response && error.response.data ? error.response.data.message : this.$t('error_saving_task'))
      } finally {
        this.loading = false
      }
    },
    async updateAssignments(taskId) {
      if (!this.canEditTask) return
      const currentAssignments = this.task.assignments || []
      const newAssignments = this.form.assigned_users.filter(item => item.user_id)
      const newUserIds = newAssignments.map(item => String(item.user_id))
      for (const assignment of currentAssignments) {
        if (!newUserIds.includes(String(assignment.user_id))) await helper.customDelete(`${process.env.VUE_APP_API}/tasks/${taskId}/users/${assignment.user_id}`)
      }
      for (const assignedUser of newAssignments) {
        const existing = currentAssignments.find(item => String(item.user_id) === String(assignedUser.user_id))
        if (existing && existing.role !== assignedUser.role) {
          await helper.customDelete(`${process.env.VUE_APP_API}/tasks/${taskId}/users/${assignedUser.user_id}`)
        }
        if (!existing || existing.role !== assignedUser.role) {
          await helper.customPost(`${process.env.VUE_APP_API}/tasks/${taskId}/assign`, assignedUser)
        }
      }
    },
    async reloadTask() {
      if (!this.task) return
      const response = await helper.customGet(`${process.env.VUE_APP_API}/tasks/${this.task.id}`)
      Object.assign(this.task, response.data)
      this.subtasks = (response.data.subtasks || []).map(subtask => ({ ...subtask }))
    },
    async addComment() {
      if (!this.canEditTask || !this.task || !this.newComment.trim() || this.sendingComment) return
      this.sendingComment = true
      try {
        await helper.customPost(`${process.env.VUE_APP_API}/tasks/${this.task.id}/comment`, { comment: this.newComment.trim() }, { showToast: true })
        this.newComment = ''
        await this.reloadTask()
        this.$emit('refresh')
      } catch (error) {
        this.$toast.error(error.response && error.response.data ? error.response.data.message : this.$t('error_adding_comment'))
      } finally {
        this.sendingComment = false
      }
    },
    openFilePicker() {
      if (this.canEditTask && this.task && !this.uploading) this.$refs.attachmentInput.click()
    },
    openFilePickerFromMenu() {
      this.showActionsMenu = false
      this.openFilePicker()
    },
    async uploadAttachment(event) {
      const file = event.target.files && event.target.files[0]
      if (!this.canEditTask || !file || !this.task) {
        event.target.value = ''
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$toast.error(this.$t('file_too_large'))
        event.target.value = ''
        return
      }
      this.uploading = true
      try {
        const business = this.$store.getters.BUSINESS
        const path = `businesses/${business.id}/tasks/${this.task.id}/${helper.generateRandString(20)}`
        await helper.customUploadFile(file, path, { entity_type: 'task', entity_id: this.task.id, notes: '' })
        await this.reloadTask()
        this.$emit('refresh')
      } catch (error) {
        this.$toast.error(error.message || this.$t('error_uploading_attachment'))
      } finally {
        this.uploading = false
        event.target.value = ''
      }
    },
    async getAttachmentUrl(attachment) {
      const response = await helper.customGet(`${process.env.VUE_APP_API}/attachments/${attachment.id}/temporary-url?response=json`)
      return response.data.url
    },
    async previewAttachment(attachment) {
      const previewWindow = window.open('', '_blank')
      try {
        const url = await this.getAttachmentUrl(attachment)
        if (previewWindow) previewWindow.location = url
        else window.open(url, '_blank', 'noopener,noreferrer')
      } catch (error) {
        if (previewWindow) previewWindow.close()
        this.$toast.error(this.$t('something_went_wrong'))
      }
    },
    async downloadAttachment(attachment) {
      const url = await this.getAttachmentUrl(attachment)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', attachment.filename)
      link.setAttribute('target', '_blank')
      link.setAttribute('rel', 'noopener noreferrer')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    async deleteAttachment(attachment) {
      if (!this.canEditTask) return
      const result = await this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('confirm_delete_attachment'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('yes_delete_it'),
        cancelButtonText: this.$t('cancel'),
        confirmButtonColor: '#dc3545'
      })
      if (!result.isConfirmed) return
      this.deletingAttachmentId = attachment.id
      try {
        await helper.customDelete(`${process.env.VUE_APP_API}/attachments/${attachment.id}`, { showToast: true })
        await this.reloadTask()
        this.$emit('refresh')
      } catch (error) {
        this.$toast.error(error.response && error.response.data ? error.response.data.message : this.$t('error_deleting_attachment'))
      } finally {
        this.deletingAttachmentId = null
      }
    },
    async deleteTask() {
      if (!this.canDeleteTask) return
      const result = await this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#64748b',
        confirmButtonText: this.$t('yes_delete_it')
      })
      if (!result.isConfirmed) return
      this.deleting = true
      try {
        await helper.customDelete(`${process.env.VUE_APP_API}/tasks/${this.task.id}`, { showToast: true })
        this.$emit('deleted', this.task.id)
        this.$emit('refresh')
        this.initialFormSnapshot = this.serializedForm()
        this.closeImmediately()
      } catch (error) {
        this.$toast.error(error.response && error.response.data ? error.response.data.message : this.$t('error_deleting_task'))
      } finally {
        this.deleting = false
      }
    },
    closeImmediately() {
      this.$emit('close')
    },
    async requestClose() {
      if (this.checkingUnsavedChanges) return
      if (!this.hasUnsavedChanges) {
        this.closeImmediately()
        return
      }
      this.checkingUnsavedChanges = true
      try {
        const result = await this.$swal.fire({
          title: this.$t('unsaved_changes'),
          text: this.$t('discard_changes_message'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('discard_changes'),
          cancelButtonText: this.$t('keep_editing'),
          confirmButtonColor: '#dc3545'
        })
        if (result.isConfirmed) this.closeImmediately()
      } finally {
        this.checkingUnsavedChanges = false
      }
    },
    handleEscape(event) {
      if (event.key === 'Escape') this.requestClose()
    },
    serializedForm() {
      return JSON.stringify({
        ...this.form,
        assigned_users: this.form.assigned_users.map(item => ({ user_id: item.user_id, role: item.role })),
        draft_subtasks: this.task ? [] : this.subtasks.map(item => ({ title: item.title, is_completed: item.is_completed }))
      })
    },
    userInitial(userId) {
      return this.userName(userId).split(' ').map(part => part.charAt(0)).join('').slice(0, 2).toUpperCase()
    },
    userName(userId) {
      const user = this.users.find(item => String(item.id) === String(userId))
      if (user && user.name) return user.name
      const assignment = this.task && this.task.assignments ? this.task.assignments.find(item => String(item.user_id) === String(userId)) : null
      return assignment && assignment.user && assignment.user.name ? assignment.user.name : this.$t('unknown')
    },
    activityInitial(activity) {
      return this.activityUserName(activity).charAt(0).toUpperCase()
    },
    activityUserName(activity) {
      return activity.user && activity.user.name ? activity.user.name : this.$t('unknown')
    },
    activityAction(activity) {
      if (activity.activity_type === 'comment') return activity.description
      if (activity.activity_type === 'assignment') return this.$t('user_assigned_to_task')
      if (activity.activity_type === 'time_log') return activity.description || this.$t('time_logged_activity')
      const descriptions = {
        'Task created': 'task_created_activity',
        'Task updated': 'task_updated_activity',
        'Subtask added': 'subtask_added_activity',
        'Subtask completed': 'subtask_completed_activity',
        'Subtask updated': 'subtask_updated_activity',
        'Subtask removed': 'subtask_removed_activity'
      }
      return descriptions[activity.description] ? this.$t(descriptions[activity.description]) : (activity.description || this.$t('task_updated_activity'))
    },
    formatDateForInput(date) {
      if (!date) return ''
      const parsedDate = new Date(date)
      const timezoneOffset = parsedDate.getTimezoneOffset() * 60000
      return new Date(parsedDate.getTime() - timezoneOffset).toISOString().split('T')[0]
    },
    formatActivityDate(date) {
      if (!date) return ''
      return new Intl.DateTimeFormat(this.$i18n.locale || undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(date))
    },
    formatHours(hours) {
      const value = Number(hours) || 0
      return `${value % 1 === 0 ? value : value.toFixed(1)}h`
    },
    statusLabel(status) {
      if (!status) return ''
      const configuredStatus = getTaskStatuses(this.$store, true).find(item => item.value === status)
      return this.translateStatus(status, configuredStatus ? configuredStatus.label : getTaskStatusLabel(this.$store, status))
    },
    translateStatus(status, fallback) {
      return this.$te(status) ? this.$t(status) : (fallback || status)
    },
    toggleActionsMenu() {
      this.showActionsMenu = !this.showActionsMenu
      this.showTimeTracker = false
    },
    toggleTimeTracker() {
      if (!this.canEditTask || !this.task) return
      this.showTimeTracker = !this.showTimeTracker
      this.showActionsMenu = false
    },
    toggleMoreDetails() {
      this.showMoreDetails = !this.showMoreDetails
      this.showActionsMenu = false
    },
    async logTime() {
      if (!this.canEditTask || !this.task || !this.timeLog.hours || Number(this.timeLog.hours) <= 0) return
      this.loggingTime = true
      try {
        await helper.customPost(`${process.env.VUE_APP_API}/tasks/${this.task.id}/log-time`, {
          hours: Number(this.timeLog.hours),
          description: this.timeLog.description
        })
        this.timeLog = { hours: '', description: '' }
        this.showTimeTracker = false
        await this.reloadTask()
        this.$emit('refresh')
        this.$toast.success(this.$t('time_logged'))
      } catch (error) {
        this.$toast.error(error.response && error.response.data ? error.response.data.message : this.$t('error_logging_time'))
      } finally {
        this.loggingTime = false
      }
    },
    cancelSubtaskInput() {
      this.showSubtaskInput = false
      this.newSubtaskTitle = ''
    },
    async addSubtask() {
      if (!this.canModifyTask) return
      const title = this.newSubtaskTitle.trim()
      if (!title || this.savingSubtask) return
      this.savingSubtask = true
      try {
        if (this.task) {
          await helper.customPost(`${process.env.VUE_APP_API}/tasks/${this.task.id}/subtasks`, { title })
          await this.reloadTask()
          this.$emit('refresh')
        } else {
          this.subtasks.push({
            id: `draft-${Date.now()}`,
            title,
            is_completed: false,
            position: this.subtasks.length + 1,
            _draft: true
          })
        }
        this.cancelSubtaskInput()
      } catch (error) {
        this.$toast.error(error.response && error.response.data ? error.response.data.message : this.$t('error_saving_subtask'))
      } finally {
        this.savingSubtask = false
      }
    },
    async toggleSubtask(subtask, completed) {
      if (!this.canModifyTask) return
      const previousValue = subtask.is_completed
      this.$set(subtask, 'is_completed', completed)
      if (subtask._draft || !this.task) return
      this.togglingSubtaskId = subtask.id
      try {
        await helper.customPatch(`${process.env.VUE_APP_API}/tasks/${this.task.id}/subtasks/${subtask.id}`, { is_completed: completed })
        await this.reloadTask()
        this.$emit('refresh')
      } catch (error) {
        this.$set(subtask, 'is_completed', previousValue)
        this.$toast.error(error.response && error.response.data ? error.response.data.message : this.$t('error_saving_subtask'))
      } finally {
        this.togglingSubtaskId = null
      }
    },
    async deleteSubtask(subtask) {
      if (!this.canModifyTask) return
      this.pendingSubtaskRemovalId = null
      if (subtask._draft || !this.task) {
        this.subtasks = this.subtasks.filter(item => item.id !== subtask.id)
        return
      }
      try {
        await helper.customDelete(`${process.env.VUE_APP_API}/tasks/${this.task.id}/subtasks/${subtask.id}`)
        await this.reloadTask()
        this.$emit('refresh')
      } catch (error) {
        this.$toast.error(error.response && error.response.data ? error.response.data.message : this.$t('error_deleting_subtask'))
      }
    },
    hexToRgba(hex, opacity) {
      if (!hex || !hex.startsWith('#')) return `rgba(100, 116, 139, ${opacity})`
      let value = hex.slice(1)
      if (value.length === 3) value = value.split('').map(char => char + char).join('')
      const number = parseInt(value, 16)
      return `rgba(${(number >> 16) & 255}, ${(number >> 8) & 255}, ${number & 255}, ${opacity})`
    }
  }
}
</script>

<style scoped>
.task-editor-backdrop { overflow: hidden; overscroll-behavior: contain; background: rgba(15, 23, 42, 0.58); backdrop-filter: blur(3px); padding: 18px; }
.task-editor-dialog { width: min(1150px, calc(100vw - 48px)); max-width: none; min-height: calc(100vh - 36px); margin: auto; }
.task-editor-modal { max-height: calc(100vh - 48px); overflow: hidden; border: 1px solid rgba(226, 232, 240, 0.85); border-radius: 16px; background: #fff; box-shadow: 0 24px 64px rgba(15, 23, 42, 0.22); }
.task-editor-form { display: flex; max-height: calc(100vh - 48px); min-height: 0; flex-direction: column; }
.task-editor-header { position: relative; display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 20px 22px 12px; }
.task-heading { display: flex; min-width: 0; flex: 1; align-items: center; gap: 13px; }
.task-check { display: grid; width: 48px; height: 48px; flex: 0 0 48px; place-items: center; border-radius: 11px; background: linear-gradient(145deg, #eff6ff, #dbeafe); color: #1264e8; font-size: 22px; }
.task-check.is-complete { background: linear-gradient(145deg, #e9fff2, #d1fae5); color: #0f9f5f; }
.task-heading-copy { min-width: 0; }
.task-title-input { width: min(460px, 42vw); border: 0; outline: 0; background: transparent; color: #111827; font-size: 22px; font-weight: 750; line-height: 1.2; }
.task-title-input::placeholder { color: #9ca3af; }
.task-project-name { display: flex; align-items: center; gap: 7px; margin-top: 4px; color: #718096; font-size: 12px; font-weight: 500; }
.task-header-actions { display: flex; flex: 0 0 auto; align-items: center; gap: 8px; }
.task-header-action, .task-icon-button { display: inline-flex; min-width: 36px; height: 36px; align-items: center; justify-content: center; gap: 6px; border: 0; border-radius: 9px; background: transparent; color: #46566f; font-size: 13px; font-weight: 650; transition: background-color .2s ease, color .2s ease; }
button.task-header-action:hover:not(:disabled), .task-icon-button:hover, .task-icon-button.active { background: #f1f5f9; color: #0f5ddb; }
.task-header-action:disabled { cursor: not-allowed; opacity: .48; }
.task-time-summary { padding: 0 8px; }
.task-header-action i, .task-icon-button i { font-size: 17px; }
.task-action-divider { width: 1px; height: 28px; margin: 0 4px; background: #e2e8f0; }
.task-close-button:hover { background: #fff1f2; color: #e11d48; }
.task-header-popover { position: absolute; z-index: 8; top: 62px; right: 58px; border: 1px solid #dbe3ed; border-radius: 11px; background: #fff; box-shadow: 0 14px 32px rgba(15, 23, 42, .16); }
.time-tracker-popover { display: grid; width: 260px; gap: 10px; padding: 14px; }
.time-tracker-popover strong { color: #17233a; font-size: 13px; }
.time-tracker-popover label { display: grid; gap: 4px; margin: 0; color: #607089; font-size: 11px; font-weight: 650; }
.time-tracker-popover input { width: 100%; height: 34px; border: 1px solid #dbe3ed; border-radius: 8px; outline: 0; color: #26354c; padding: 0 9px; font-size: 12px; }
.time-tracker-popover input:focus { border-color: #8bb5f8; box-shadow: 0 0 0 3px rgba(37, 99, 235, .09); }
.time-tracker-popover .btn { justify-self: end; }
.task-actions-menu { width: 190px; padding: 6px; }
.task-actions-menu button { display: flex; width: 100%; align-items: center; gap: 9px; border: 0; border-radius: 7px; background: transparent; color: #34455e; padding: 9px 10px; font-size: 12px; text-align: left; }
.task-actions-menu button:hover:not(:disabled) { background: #f1f5f9; color: #1264e8; }
.task-actions-menu button:disabled { opacity: .5; }
.task-meta-row { display: flex; flex-wrap: wrap; gap: 9px; padding: 2px 22px 16px; }
.task-pill { display: inline-flex; height: 38px; align-items: center; gap: 7px; margin: 0; padding: 0 13px; border-radius: 11px; font-size: 12px; font-weight: 650; }
.task-pill select, .task-pill input { min-width: 0; border: 0; outline: 0; background: transparent; color: inherit; font: inherit; cursor: pointer; }
.task-pill select { appearance: none; padding-right: 1px; }
.status-pill { background: var(--task-status-bg); color: var(--task-status-color); }
.priority-pill.priority-low { background: #ecfeff; color: #0891b2; }
.priority-pill.priority-medium { background: #eff6ff; color: #2563eb; }
.priority-pill.priority-high { background: #fff7ed; color: #ea580c; }
.priority-pill.priority-urgent { background: #fff1f2; color: #e94151; }
.date-pill { background: #f1f5f9; color: #27364d; }
.pill-chevron { font-size: 12px; }
.task-editor-body { display: grid; min-height: 0; flex: 1; grid-template-columns: minmax(0, 55fr) minmax(0, 45fr); overflow-y: auto; overscroll-behavior: contain; border-top: 1px solid #f1f5f9; }
.task-details-column, .task-activity-column { min-width: 0; padding: 20px 24px; }
.task-details-column { border-right: 1px solid #e6ebf2; }
.task-section { margin-bottom: 18px; }
.task-section-title, .task-field-label, .progress-heading label { display: block; margin-bottom: 8px; color: #162238; font-size: 14px; font-weight: 750; letter-spacing: -.01em; }
.task-description-input, .task-notes-input { width: 100%; resize: vertical; border: 1px solid transparent; border-radius: 10px; outline: 0; background: #f4f7fb; color: #26354c; font-size: 13px; line-height: 1.45; padding: 12px; transition: border-color .2s ease, box-shadow .2s ease, background-color .2s ease; }
.task-description-input:focus, .task-notes-input:focus { border-color: #9fc2ff; background: #fff; box-shadow: 0 0 0 3px rgba(37, 99, 235, .1); }
.task-field-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 16px; }
.task-field-label { margin-bottom: 7px; color: #50617a; font-size: 12px; }
.date-control { display: flex; width: 100%; min-height: 43px; align-items: center; gap: 8px; border: 1px solid #dbe3ed; border-radius: 9px; background: #fff; color: #25344b; padding: 5px 8px; }
.date-control input { min-width: 0; flex: 1; border: 0; outline: 0; background: transparent; color: #25344b; font-size: 12px; font-weight: 600; }
.user-avatar, .comment-avatar, .activity-avatar { display: inline-grid; place-items: center; border-radius: 50%; background: linear-gradient(145deg, #0c66e4, #0751be); color: #fff; font-weight: 750; }
.user-avatar { width: 26px; height: 26px; flex: 0 0 26px; font-size: 10px; }
.assignee-chips { display: flex; min-height: 36px; flex-wrap: wrap; align-items: flex-start; gap: 6px; }
.assignee-chip-wrap { position: relative; max-width: 100%; }
.assignee-chip { display: flex; max-width: 100%; align-items: center; border: 1px solid #dbe3ed; border-radius: 999px; background: #fff; padding: 3px; transition: border-color .18s ease, box-shadow .18s ease; }
.assignee-chip.active { border-color: #8bb5f8; box-shadow: 0 0 0 2px rgba(37, 99, 235, .08); }
.assignee-chip.removing { border-color: #fecdd3; background: #fff7f8; }
.assignee-chip-main { display: flex; min-width: 0; align-items: center; gap: 7px; border: 0; background: transparent; color: #26354c; padding: 0 4px 0 0; font-size: 11px; font-weight: 650; }
.assignee-chip-main > span:last-child { overflow: hidden; max-width: 118px; text-overflow: ellipsis; white-space: nowrap; }
.remove-assignment, .confirm-chip-action, .cancel-chip-action { display: grid; width: 24px; height: 24px; flex: 0 0 24px; place-items: center; border: 0; border-radius: 50%; background: transparent; color: #94a3b8; }
.remove-assignment:hover { background: #fff1f2; color: #e11d48; }
.confirm-chip-action { background: #dcfce7; color: #15803d; }
.cancel-chip-action:hover { background: #f1f5f9; color: #475569; }
.add-assignee-button { display: grid; width: 34px; height: 34px; flex: 0 0 34px; place-items: center; border: 1px dashed #aebbd0; border-radius: 50%; background: #fff; color: #2563eb; font-size: 18px; }
.add-assignee-button:hover { border-color: #6096ec; background: #eff6ff; }
.role-editor { position: absolute; z-index: 6; top: calc(100% + 6px); left: 6px; display: grid; width: 156px; gap: 5px; border: 1px solid #dbe3ed; border-radius: 9px; background: #fff; box-shadow: 0 10px 24px rgba(15, 23, 42, .14); padding: 9px; }
.role-editor label { margin: 0; color: #607089; font-size: 10px; font-weight: 700; }
.role-editor select, .assignee-picker select { min-width: 0; height: 34px; border: 1px solid #dbe3ed; border-radius: 7px; outline: 0; background: #fff; color: #26354c; padding: 0 8px; font-size: 11px; }
.assignee-picker { display: flex; align-items: center; gap: 7px; margin-top: 7px; }
.assignee-picker select { flex: 1; }
.empty-assignee-copy { display: block; margin-top: 3px; color: #94a3b8; font-size: 11px; }
.date-control i { color: #3d5678; font-size: 17px; }
.subtasks-section { margin-top: 18px; padding-top: 16px; border-top: 1px solid #e9eef5; }
.subtasks-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.subtasks-heading > div { display: flex; align-items: baseline; gap: 8px; }
.subtasks-heading h6 { margin: 0; color: #162238; font-size: 14px; font-weight: 750; }
.subtasks-heading span { color: #708198; font-size: 11px; font-weight: 700; }
.add-subtask-action { display: inline-flex; align-items: center; gap: 4px; border: 0; background: transparent; color: #2467d8; padding: 3px 0; font-size: 11px; font-weight: 700; }
.add-subtask-action i { font-size: 15px; }
.subtask-progress { height: 5px; overflow: hidden; margin-top: 10px; border-radius: 999px; background: #e9eef5; }
.subtask-progress span { display: block; height: 100%; border-radius: inherit; background: #2878ee; transition: width .2s ease; }
.subtask-list { margin-top: 8px; }
.subtask-item { display: grid; min-height: 32px; grid-template-columns: 18px minmax(0, 1fr) auto auto; align-items: center; gap: 7px; color: #334155; font-size: 12px; }
.subtask-item input { width: 15px; height: 15px; margin: 0; accent-color: #1264e8; }
.subtask-item.completed .subtask-title { color: #8b98aa; text-decoration: line-through; }
.subtask-remove, .subtask-confirm, .subtask-cancel { display: grid; width: 26px; height: 26px; place-items: center; border: 0; border-radius: 7px; background: transparent; color: #94a3b8; }
.subtask-remove:hover { background: #fff1f2; color: #e11d48; }
.subtask-confirm { background: #dcfce7; color: #15803d; }
.subtask-cancel:hover { background: #f1f5f9; color: #475569; }
.subtask-input-row { display: flex; align-items: center; gap: 7px; margin-top: 9px; }
.subtask-input-row > input { min-width: 0; height: 36px; flex: 1; border: 1px solid #dbe3ed; border-radius: 8px; outline: 0; color: #26354c; padding: 0 10px; font-size: 12px; }
.subtask-input-row > input:focus { border-color: #8bb5f8; box-shadow: 0 0 0 3px rgba(37, 99, 235, .09); }
.task-more-details, .task-attachments { margin-top: 18px; padding-top: 16px; border-top: 1px solid #e9eef5; }
.progress-heading, .activity-heading { display: flex; align-items: center; justify-content: space-between; }
.progress-heading label { margin: 0; font-size: 13px; }
.progress-heading span, .activity-heading > span { color: #708198; font-size: 13px; font-weight: 650; }
.attachment-list { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.attachment-item { display: flex; max-width: 280px; min-width: 0; align-items: center; border: 1px solid #dce5f0; border-radius: 9px; background: #fff; padding: 3px; font-size: 12px; }
.attachment-item > a { display: inline-flex; min-width: 0; flex: 1; align-items: center; gap: 7px; color: #3d5575; padding: 5px 6px; text-decoration: none; }
.attachment-item > a span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.attachment-private { color: #64748b; font-size: 10px; }
.attachment-item > button { display: grid; width: 27px; height: 27px; flex: 0 0 27px; place-items: center; border: 0; border-radius: 7px; background: transparent; color: #718096; }
.attachment-item > button:hover:not(:disabled) { background: #eff6ff; color: #1264e8; }
.attachment-item > .attachment-delete:hover:not(:disabled) { background: #fff1f2; color: #e11d48; }
.task-activity-column { display: flex; flex-direction: column; }
.comment-composer { overflow: hidden; border: 1px solid #dbe3ed; border-radius: 11px; background: #fff; box-shadow: 0 1px 2px rgba(15, 23, 42, .03); }
.comment-composer.disabled { background: #f8fafc; }
.comment-composer > .comment-avatar { float: left; width: 32px; height: 32px; margin: 11px 0 0 11px; font-size: 11px; }
.comment-composer textarea { width: calc(100% - 56px); min-height: 58px; resize: none; border: 0; outline: 0; background: transparent; color: #26354c; padding: 13px 11px; font-size: 12px; }
.comment-actions { display: flex; clear: both; align-items: center; justify-content: space-between; padding: 5px 9px 9px; }
.comment-tools { display: flex; align-items: center; gap: 10px; }
.comment-tools button { display: grid; width: 34px; height: 34px; place-items: center; border: 0; border-radius: 8px; background: transparent; color: #536985; font-size: 19px; }
.comment-tools button:hover:not(:disabled) { background: #eff6ff; color: #1264e8; }
.comment-hint { color: #9aa8ba; font-size: 11px; }
.send-comment { min-width: 76px; height: 34px; border: 0; border-radius: 8px; background: #1264e8; color: #fff; font-size: 12px; font-weight: 700; }
.send-comment:disabled { background: #b8cdf4; }
.activity-section { min-height: 0; flex: 1; padding-top: 12px; }
.activity-heading { margin-bottom: 10px; }
.activity-list { max-height: 270px; overflow-y: auto; padding-right: 7px; }
.activity-list.expanded { max-height: none; overflow-y: visible; }
.activity-item { display: flex; gap: 10px; padding: 11px 0; }
.activity-item + .activity-item { border-top: 1px solid #edf1f6; }
.activity-avatar { width: 32px; height: 32px; flex: 0 0 32px; margin-top: 2px; font-size: 11px; }
.activity-copy { min-width: 0; flex: 1; }
.activity-meta { display: flex; flex-wrap: wrap; align-items: baseline; gap: 7px; }
.activity-meta strong { color: #19263b; font-size: 12px; }
.activity-meta time { color: #8796aa; font-size: 11px; }
.activity-copy p { margin: 3px 0 0; color: #516177; font-size: 11px; line-height: 1.4; }
.status-change { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 5px; color: #5e6c80; font-size: 11px; }
.status-change .activity-action-label { color: #607089; }
.status-change .status-badge { border-radius: 999px; background: #eef2f6; padding: 3px 8px; font-weight: 650; }
.status-change .new-status { background: #e8f1ff; color: #1763ce; }
.hours-logged { display: inline-flex; align-items: center; gap: 5px; margin-top: 7px; border-radius: 999px; background: #ecfdf5; color: #14805e; padding: 4px 9px; font-size: 11px; font-weight: 650; }
.empty-activity { display: grid; min-height: 120px; place-items: center; align-content: center; color: #98a6b8; text-align: center; }
.empty-activity i { font-size: 28px; }
.empty-activity p { margin: 8px 0 0; font-size: 13px; }
.history-toggle { display: inline-flex; align-items: center; gap: 6px; margin-top: 7px; border: 0; background: transparent; color: #2467d8; padding: 4px 0; font-size: 11px; font-weight: 700; }
.task-editor-footer { display: flex; align-items: center; justify-content: space-between; border-top: 1px solid #e6ebf2; padding: 12px 20px; }
.footer-actions { display: flex; gap: 12px; }
.delete-task-button { display: inline-flex; min-width: 96px; height: 40px; align-items: center; justify-content: center; gap: 7px; border: 1px solid #fecdd3; border-radius: 9px; background: #fff1f2; color: #e03448; font-size: 12px; font-weight: 700; }
.delete-task-button:disabled { cursor: not-allowed; opacity: .6; }
.details-slide-enter-active, .details-slide-leave-active { transition: opacity .2s ease, transform .2s ease; }
.details-slide-enter, .details-slide-leave-to { opacity: 0; transform: translateY(-6px); }

@media (max-width: 900px) {
  .task-editor-header { align-items: flex-start; }
  .task-header-actions { flex-wrap: wrap; justify-content: flex-end; gap: 2px; }
  .task-editor-body { grid-template-columns: 1fr; }
  .task-details-column { border-right: 0; border-bottom: 1px solid #e6ebf2; }
}

@media (max-width: 575.98px) {
  .task-editor-backdrop { padding: 0; }
  .task-editor-dialog { width: 100%; min-height: 100vh; margin: 0; }
  .task-editor-modal, .task-editor-form { min-height: 100vh; max-height: 100vh; border-radius: 0; }
  .task-editor-header { padding: 20px 18px 14px; }
  .task-check { width: 48px; height: 48px; flex-basis: 48px; font-size: 22px; }
  .task-title-input { width: 100%; font-size: 21px; }
  .task-action-divider { display: none; }
  .task-header-action, .task-icon-button { min-width: 32px; height: 32px; }
  .task-header-actions { max-width: 132px; }
  .task-header-popover { top: 62px; right: 18px; max-width: calc(100vw - 36px); }
  .task-meta-row { padding: 4px 18px 18px; }
  .task-pill { height: 41px; padding: 0 12px; font-size: 12px; }
  .task-details-column, .task-activity-column { padding: 24px 18px; }
  .task-field-grid { grid-template-columns: 1fr; gap: 16px; }
  .assignee-picker { align-items: stretch; flex-direction: column; }
  .comment-hint { display: none; }
  .task-editor-footer { position: sticky; bottom: 0; z-index: 2; gap: 10px; background: #fff; padding: 14px 16px; }
  .delete-task-button, .footer-actions .btn { min-width: 0; height: 44px; padding: 0 15px; }
}
</style>
